import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { BanknotesIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { validateTiers } from '@claimup/shared/helpers/ar-pricing';
import api, { getErrorMessage } from '../../services/api';

/**
 * «Precios AR» en el perfil de una empresa (docs/ar-precios-tramos-plan.md). Solo
 * lo monta AccountPanel para el admin: la empresa ve en su Facturación AR lo que
 * le toca, no la tabla.
 *
 * Un precio fijo al mes por tramo de euros gestionados, sin IVA. El «desde» de
 * cada tramo es el «hasta» del anterior, así que no se escribe: no puede haber
 * huecos. Las reglas, en libs/shared/helpers/ar-pricing.js.
 */

// En el formulario los importes son texto: se convierten al guardar.
const toRows = tiers => tiers.map(t => ({ to: t.to === null ? '' : String(t.to), price: String(t.price) }));

/** Las filas del formulario a tramos: cada «desde» sale del «hasta» anterior. */
export const rowsToTiers = rows =>
  rows.map((r, i) => ({
    from: i === 0 ? 0 : Number(rows[i - 1].to),
    to: r.to === '' ? null : Number(r.to),
    price: r.price === '' ? NaN : Number(r.price),
  }));

const eur = n =>
  new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 }).format(n || 0);

const ArPricingBlock = ({ companyId }) => {
  const [rows, setRows] = useState([{ to: '', price: '' }]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get(`/ar-billing/companies/${companyId}/pricing`)
      .then(({ data }) => {
        if (data.data.tiers.length) setRows(toRows(data.data.tiers));
        setMeta(data.data);
      })
      .catch(err => toast.error(getErrorMessage(err)))
      .finally(() => setLoading(false));
  }, [companyId]);

  const tiers = rowsToTiers(rows);
  const errors = validateTiers(tiers);

  const update = (i, field, value) => setRows(rs => rs.map((r, k) => (k === i ? { ...r, [field]: value } : r)));
  const add = () => setRows(rs => [...rs, { to: '', price: '' }]);
  const remove = i => setRows(rs => rs.filter((_, k) => k !== i));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/ar-billing/companies/${companyId}/pricing`, { tiers });
      setMeta(data.data);
      toast.success('Precios AR guardados');
    } catch (err) {
      toast.error(err?.response?.data?.error || getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className='bg-white rounded-lg border border-slate-200 overflow-hidden'>
      <div className='px-6 py-4 border-b border-slate-200 bg-slate-50 flex items-center'>
        <BanknotesIcon className='h-5 w-5 text-blue-600 mr-2' />
        <h3 className='text-lg font-medium text-slate-900'>Precios AR</h3>
        <span className='ml-2 text-xs text-slate-500'>Solo lo ve ClaimUp</span>
      </div>

      <div className='p-6 space-y-4'>
        <p className='text-sm text-slate-600'>
          Precio fijo al mes, sin IVA, según el tramo en el que caen los euros gestionados del mes. La empresa ve en su
          Facturación AR el tramo y el importe que le toca. Un cambio aquí vale para el mes en curso y los siguientes:
          los meses cerrados se quedan con el precio que tenían.
        </p>

        {loading ? (
          <p className='text-sm text-slate-500'>Cargando…</p>
        ) : (
          <>
            <table className='min-w-full text-sm'>
              <thead>
                <tr className='text-left text-xs uppercase text-slate-500'>
                  <th className='py-2 pr-3'>Desde</th>
                  <th className='py-2 pr-3'>Hasta</th>
                  <th className='py-2 pr-3'>Precio al mes (sin IVA)</th>
                  <th className='py-2' />
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const last = i === rows.length - 1;
                  return (
                    <tr key={i}>
                      <td className='py-1.5 pr-3 tabular-nums text-slate-600'>{eur(tiers[i].from)}</td>
                      <td className='py-1.5 pr-3'>
                        <input
                          type='number'
                          min='0'
                          step='0.01'
                          value={r.to}
                          onChange={e => update(i, 'to', e.target.value)}
                          placeholder={last ? 'Sin límite' : ''}
                          aria-label={`Hasta, tramo ${i + 1}`}
                          className='w-40 rounded-lg border border-slate-300 px-2 py-1'
                        />
                      </td>
                      <td className='py-1.5 pr-3'>
                        <input
                          type='number'
                          min='0'
                          step='0.01'
                          value={r.price}
                          onChange={e => update(i, 'price', e.target.value)}
                          aria-label={`Precio, tramo ${i + 1}`}
                          className='w-40 rounded-lg border border-slate-300 px-2 py-1'
                        />
                      </td>
                      <td className='py-1.5'>
                        {rows.length > 1 && (
                          <button
                            type='button'
                            onClick={() => remove(i)}
                            className='text-slate-400 hover:text-red-600'
                            aria-label={`Quitar tramo ${i + 1}`}
                          >
                            <TrashIcon className='h-4 w-4' />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <button
              type='button'
              onClick={add}
              className='inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-800'
            >
              <PlusIcon className='h-4 w-4' />
              Añadir tramo
            </button>

            {errors.length > 0 && (
              <ul className='rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 space-y-0.5'>
                {errors.map(e => (
                  <li key={e}>{e}</li>
                ))}
              </ul>
            )}

            <div className='flex items-center gap-3'>
              <button
                type='button'
                onClick={save}
                disabled={saving || errors.length > 0}
                className='rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50'
              >
                {saving ? 'Guardando…' : 'Guardar precios'}
              </button>
              {meta?.updated_at && (
                <span className='text-xs text-slate-500'>
                  Última modificación: {new Date(meta.updated_at).toLocaleString('es-ES')}
                </span>
              )}
              {!meta?.updated_at && <span className='text-xs text-slate-500'>Todavía sin precios definidos.</span>}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ArPricingBlock;
